import { useAuth } from './auth';
import { preparePayAmounts, preparePayAmountsForBackend } from './preparePayAmounts';

const getBaseUrl = () => {
    const host = window.location.hostname;

    return ['fpay.net.ru', 'vercel.app', 'localhost'].some((h) => host.includes(h))
        ? 'https://dev.recurrent.net.ru/v1/'
        : 'https://recurrent.net.ru/v1/';
};

class API {
    auth = useAuth();
    access_token = this.auth?.user?.access_token;

    static baseURL = getBaseUrl();

    get authorization() {
        return 'Bearer ' + this.access_token;
    }

    get refreshToken() {
        return localStorage.getItem('refresh_token');
    }

    createRequest(path, parameters) {
        const defaultParameters = {
            headers: {
                'Content-Type': 'application/json',
            },
            method: 'POST',
        };
        const url = new URL(path, API.baseURL);

        return new Request(url, { ...defaultParameters, ...parameters });
    }

    refreshAccessToken() {
        const request = this.createRequest('./auth/jwt/refresh?refresh_token=' + this.refreshToken, {
            method: 'POST',
        });

        return fetch(request);
    }

    /**
     * Make backend fetch with credentials.
     * @param {Request} request Request object.
     * @param {number} attempt Attempt number.
     * @returns {Promise} Fetch promise result.
     */
    protectedFetch(request, attempt = 1) {
        const protectRequest = new Request(request.clone(), {
            headers: {
                Authorization: this.authorization,
            },
        });

        return new Promise((resolve, reject) => {
            fetch(protectRequest)
                .then((response) => {
                    if ([200, 201].includes(response.status)) {
                        resolve(response);
                    } else if (response.status === 401) {
                        console.info('Open new auth session.');
                        this.refreshAccessToken()
                            .then((response) => {
                                if (response.status === 200) {
                                    response
                                        .json()
                                        .then((data) => {
                                            this.auth.refreshAuth(data);
                                            this.access_token = data.access_token;

                                            // Retry
                                            if (attempt < 3) {
                                                setTimeout(() => {
                                                    resolve(this.protectedFetch(request, attempt + 1));
                                                }, 1000);
                                            } else {
                                                throw new Error('Cannot refresh auth token.');
                                            }
                                        })
                                        .catch((error) => {
                                            this.auth.signout();
                                            reject(error);
                                        });
                                } else {
                                    this.auth.signout();
                                    reject(response);
                                }
                            })
                            .catch((error) => {
                                reject(error);
                            });
                    } else {
                        reject(response);
                    }
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    /**
     * Creates error description from backend object error.
     */
    _createRequestErrorDescription(errorData) {
        const errorDetail = errorData?.detail || errorData?.message;

        if (Array.isArray(errorDetail)) {
            return errorDetail[0];
        }

        if (errorDetail) {
            return {
                loc: 'backend',
                msg: errorDetail,
            };
        }

        return {
            loc: 'client',
            msg: errorData || 'Неизвестная ошибка клиентской части',
        };
    }

    /**
     * Common method to perform protected requests with default error handler.
     * @param {object} Request object created with createRequest method.
     * @returns {Promise} Fetch promise with result.
     */
    makeRequest(request, acceptStatus = 200, prepareAmounts = true) {
        return new Promise((resolve, reject) => {
            this.protectedFetch(request)
                .then((response) => {
                    this._resolveByStatus(acceptStatus, response, resolve, reject, prepareAmounts);
                })
                .catch((error) => {
                    if (error instanceof Error) {
                        reject(this._createRequestErrorDescription(error.message));
                    } else {
                        error
                            .json()
                            .then((errorData) => {
                                reject(this._createRequestErrorDescription(errorData));
                            })
                            .catch((jsonError) => {
                                reject(this._createRequestErrorDescription(jsonError));
                            });
                    }
                });
        });
    }

    /**
     * Reject api promise with details provided by the server.
     * @param {object} response Server response object.
     * @param {function} reject Promise reject function to call.
     */
    _rejectWithDetails(response, reject) {
        response
            .json()
            .then((data) => {
                reject(data.detail);
            })
            .catch((error) => {
                reject(error);
            });
    }

    /**
     * Resolve api promise with status code.
     * @param {number} status Acceptable status code (i.e. 200, 201 ...).
     * @param {object} response Server response.
     * @param {function} resolve Promise resolve function.
     * @param {function} reject Promise reject function.
     * @param {boolean} prepareAmounts Should we check for payment amounts in given object?
     */
    _resolveByStatus(status, response, resolve, reject, prepareAmounts) {
        if (response.status === status) {
            response
                .json()
                .then((data) => {
                    if (prepareAmounts) {
                        preparePayAmounts(data);
                    }
                    resolve(data);
                })
                .catch((error) => {
                    reject(error);
                });
        } else {
            this._rejectWithDetails(response, reject);
        }
    }

    /**
     * Login user into the system, using credentials.
     * @param {Object} credentials User credentials like username and password.
     */
    login(credentials) {
        let formBody = [];
        for (const property in credentials) {
            const encodedKey = encodeURIComponent(property);
            const encodedValue = encodeURIComponent(credentials[property]);
            formBody.push(encodedKey + '=' + encodedValue);
        }
        formBody = formBody.join('&');

        const request = this.createRequest('./auth/jwt/login', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: formBody,
        });

        return fetch(request);
    }

    /**
     * Register user in the system, usign email and password.
     * @param {object} credentials User credentials including email and password.
     * @returns
     */
    register(credentials) {
        const request = this.createRequest('./auth/register', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(credentials),
        });

        return new Promise((resolve, reject) => {
            fetch(request)
                .then((response) => {
                    if (response.status === 201) {
                        response
                            .json()
                            .then((data) => {
                                if (data.access_token && data.refresh_token) {
                                    this.auth.refreshAuth(data);
                                    resolve(data);
                                } else {
                                    reject(new Error(data.detail));
                                }
                            })
                            .catch((error) => {
                                reject(error);
                            });
                    } else {
                        response
                            .json()
                            .then((data) => {
                                reject(
                                    new Error(
                                        data.detail || 'Cannot create a new account. (Status: ' + response.status + ')'
                                    )
                                );
                            })
                            .catch((error) => {
                                reject(error);
                            });
                    }
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    /**
     * Register user by email.
     * @param {string} token Invitation token.
     * @return {Promise<object>} Objec with access_token and refresh_token.
     */
    registerByEmail(token, credentials) {
        const request = this.createRequest(`./auth/register/by_email?token=${token}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(credentials),
        });

        return this.makeRequest(request, 201);
    }

    /**
     * Check email for creating a new account.
     * @param {string} email Email to check.
     * @returns {object} Information about email availability.
     */
    checkEmail(email) {
        const request = this.createRequest('./auth/check_email?email=' + encodeURIComponent(email), {
            method: 'GET',
        });

        return new Promise((resolve, reject) => {
            fetch(request)
                .then((response) => {
                    if (response.status === 200) {
                        response
                            .json()
                            .then((data) => {
                                if (data.available) {
                                    resolve(data);
                                } else {
                                    reject(data);
                                }
                            })
                            .catch((error) => {
                                reject(error);
                            });
                    } else {
                        reject(new Error('Email is not available.'));
                    }
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    sendResetPasswordEmail(email) {
        const request = this.createRequest(`./auth/reset_password/email?email=${email}`, {
            method: 'POST',
        });

        return fetch(request);
    }

    /**
     * Reset user password.
     * @param {string} token Token for password reset.
     * @param {string} password New password
     * @returns {Promise}
     */
    resetPassword(token, password) {
        const request = this.createRequest(`./auth/reset_password?token=${token}&password=${password}`, {
            method: 'PATCH',
        });

        return fetch(request);
    }

    /**
     * Get user companies using authorization.
     * @returns {array} Array of user companies.
     */
    getCompanies() {
        const request = this.createRequest('./companies/', { method: 'GET' });

        return this.makeRequest(request);
    }

    /**
     * Create a new company.
     * @param {object} data Company information.
     * @returns
     */
    createCompany(data) {
        const request = this.createRequest(
            './companies/',
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
            },
            true
        );

        return this.makeRequest(request, 201, false);
    }

    /**
     * Check if given short url is in use.
     * @param {string} url Company custom url.
     * @returns {Promise} Promise fetchin existing status.
     */
    checkCompanyUrl(url) {
        const request = this.createRequest('./companies/exist/url/' + encodeURIComponent(url), {
            method: 'GET',
        });

        return this.makeRequest(request);
    }

    /**
     * Get company information to display on the Setting tab.
     * @param {number} id Company id.
     * @returns {object} Company information including: name, url, email (owner)
     */
    getCompanySettings(id) {
        const request = this.createRequest('./companies/' + id, {
            method: 'GET',
        });

        return this.makeRequest(request);
    }

    /**
     * Update company settings including name and short url.
     * @param {number} id Company id.
     * @param {object} settings Company settings (name, url)
     * @returns
     */
    updateCompanySetting(id, settings) {
        const request = this.createRequest('./companies/' + id, {
            method: 'PATCH',
            body: JSON.stringify(settings),
        });

        return this.makeRequest(request);
    }

    /**
     * Add a new user to company.
     * @param {number} companyId Company id.
     * @param {object} user User data.
     * @returns {Promise<object>} Created user object.
     */
    addUserToCompany(companyId, user) {
        const request = this.createRequest(`./companies/${companyId}/add_user`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(user),
        });

        return this.makeRequest(request, 201);
    }

    /**
     * Delete user from company.
     * @param {number} companyId Company Id.
     * @param {number} userId User Id.
     * @returns {Promise<object>} Deleted user object.
     */
    deleteUserFromCompany(companyId, userId) {
        const request = this.createRequest(`./companies/${companyId}/user?user_id=${userId}`, {
            method: 'DELETE',
        });

        return this.makeRequest(request, 200);
    }

    // @todo Update user in company method.

    /**
     * Load company's payment forms.
     * @param {number} companyId ID of company to load pages.
     * @returns Company payment pages.
     */
    getForms(companyId) {
        const request = this.createRequest(`./company/${companyId}/forms/`, {
            method: 'GET',
        });

        return new Promise((resolve, reject) => {
            this.protectedFetch(request)
                .then((response) => {
                    if (response.status === 200) {
                        response
                            .json()
                            .then((data) => {
                                const { forms } = data;

                                if (Array.isArray(forms)) {
                                    forms.forEach((form) => {
                                        form.default_regular_amount /= 100;
                                        form.default_first_amount /= 100;
                                    });
                                }

                                resolve(data);
                            })
                            .catch((error) => {
                                reject(error);
                            });
                    } else {
                        this._rejectWithDetails(response, reject);
                    }
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    /**
     * Create a new payment form.
     * @param {number} companyId ID of company.
     * @param {object} settings Payment form settings.
     * @returns Created form.
     */
    createForm(companyId, settings) {
        this._prepareFormSettings(settings);

        const request = this.createRequest(`./company/${companyId}/forms/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(settings),
        });

        return this.makeRequest(request, 201);
    }

    /**
     * Delete payemnt form from company.
     * @param {number} companyId Company ID.
     * @param {number} formId Payment form ID.
     * @returns
     */
    deleteForm(companyId, formId) {
        const request = this.createRequest(`./company/${companyId}/forms/${formId}`, {
            method: 'DELETE',
        });

        return this.makeRequest(request, 200);
    }

    /**
     * Update payment form settings.
     * @param {number} companyId Company id.
     * @param {number} formId Form id to update.
     * @param {object} settings Form Settings.
     * @returns
     */
    updateForm(companyId, formId, settings) {
        this._prepareFormSettings(settings);

        const request = this.createRequest(`./company/${companyId}/forms/${formId}`, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(settings),
        });

        return this.makeRequest(request);
    }

    /**
     * This method transforms form amounts (first, regular) to pennies.
     * @param {object} settings Form settings.
     */
    _prepareFormSettings(settings) {
        if (settings.default_first_amount !== undefined) {
            settings.default_first_amount *= 100;
        }

        if (settings.default_regular_amount !== undefined) {
            settings.default_regular_amount *= 100;
        }
    }

    /**
     * Remove external field from form.
     * @param {number} companyId Company id.
     * @param {number} formId Form id.
     * @param {number} fieldId External field id.
     * @returns
     */
    deleteExternalField(companyId, formId, fieldId) {
        const request = this.createRequest(`./company/${companyId}/forms/${formId}/external_field/${fieldId}`, {
            method: 'DELETE',
        });

        return this.makeRequest(request);
    }

    /**
     * Create form customization.
     * @param {number} companyId Company id.
     * @param {number} formId Form id.
     * @param {object} settings Form customization settings.
     * @returns Api execution promise.
     */
    createFormCustomization(companyId, formId, settings) {
        this._prepareFormCustomizationSettings(settings);

        const request = this.createRequest(`./company/${companyId}/forms/${formId}/customization`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(settings),
        });

        return this.makeRequest(request);
    }

    _prepareFormCustomizationSettings(settings) {
        settings.regular_amount *= 100;
        settings.first_amount *= 100;
    }

    /**
     * Get payment form by customization link.
     * @param {string} link Form customization link.
     * @returns Payment form data object.
     */
    getForm(link) {
        const request = this.createRequest(`./form/customization/${link}`, {
            method: 'GET',
        });

        return this.makeRequest(request);
    }

    /**
     * Perform payment using payment form, opened by link.
     * @param {string} link Payment form short link.
     * @param {object} formData Payment form data (email, phone, etc)
     * @returns Link to payment service.
     */
    performPayment(link, formData) {
        const request = this.createRequest(`./form/customization/${link}/perform_payment`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(formData),
        });

        return this.makeRequest(request);
    }

    /**
     * Perform credit payment. Only for yookassa terminals.
     * @param {strin} link Payment form short link.
     * @param {object} formData Payment form data (email, phone, etc)
     * @returns
     */
    performCreditPayment(link, formData) {
        const request = this.createRequest(`./form/customization/${link}/perform_credit_payment`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(formData),
        });

        return this.makeRequest(request);
    }

    /**
     * Get available payment services.
     * @param {number} companyId Company id.
     * @returns {object} Array of payment_services.
     */
    getPaymentServices(companyId) {
        const request = this.createRequest(`./company/${companyId}/terminals/payment_services`, {
            method: 'GET',
        });

        return this.makeRequest(request);
    }

    /**
     * Get terminal settings.
     * @param {number} companyId Company id.
     * @param {number} terminalId Terminal id.
     * @returns {object} Terminal settings.
     */
    getTerminal(companyId, terminalId) {
        const request = this.createRequest(`./company/${companyId}/terminals/${terminalId}`, {
            method: 'GET',
        });

        return this.makeRequest(request);
    }

    /**
     * Create terminal with provided settings.
     * @param {number} companyId Company id.
     * @param {object} settings Terminal settings.
     * @returns
     */
    createTerminal(companyId, serviceId, settings, isYookassa) {
        const yookassaPath = isYookassa ? 'yookassa' : '';
        const request = this.createRequest(
            `./company/${companyId}/terminals/${yookassaPath}?payment_service_id=${serviceId}`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(settings),
            }
        );

        return this.makeRequest(request, 201);
    }

    /**
     * Update terminal settings.
     * @param {number} companyId Company id.
     * @param {number} terminalId Terminal id.
     * @param {object} settings Terminal settings.
     * @returns
     */
    updateTerminal(companyId, terminalId, settings) {
        const request = this.createRequest(`./company/${companyId}/terminals/${terminalId}`, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(settings),
        });

        return this.makeRequest(request);
    }

    /**
     * Delete terminal from company.
     * @param {number} companyId Company id.
     * @param {number} terminalId Terminal id.
     * @returns {Promise} Object with deleted terminal.
     */
    deleteTerminal(companyId, terminalId) {
        const request = this.createRequest(`./company/${companyId}/terminals/${terminalId}`, {
            method: 'DELETE',
        });

        return this.makeRequest(request);
    }

    /**
     * Send auth-code from Yookassa to obtain Access Token.
     * @param {*} companyId Company id.
     * @param {*} terminalId Terminal id.
     * @param {*} tokenData Object with auth-code.
     * @returns {Promise} Updated terminal state.
     */
    postConfirmationToken(companyId, terminalId, tokenData) {
        const request = this.createRequest(`./company/${companyId}/terminals/confirmation/${terminalId}`, {
            method: 'POST',
            body: JSON.stringify(tokenData),
        });

        return this.makeRequest(request);
    }

    /**
     * Perform test payment. Use this only for Yookassa terminals.
     * @param {number} companyId Company id.
     * @param {number} terminalId Terminal id.
     * @returns {Promise} Payment result.
     */
    performTestPayment(companyId, terminalId) {
        const request = this.createRequest(`./company/${companyId}/terminals/test_payment/${terminalId}`, {
            method: 'POST',
        });

        return this.makeRequest(request);
    }

    /**
     * Get company clients.
     * @param {number} companyId Company id.
     * @returns Company clients array.
     */
    getClients(companyId, params) {
        const query = [];
        for (const key in params) {
            if (Object.prototype.hasOwnProperty.call(params, key)) {
                query.push(`${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`);
            }
        }

        const request = this.createRequest(`./company/${companyId}/clients/?${query.join('&')}`, {
            method: 'GET',
        });

        return this.makeRequest(request);
    }

    /**
     * Update client settings (email, name, phone).
     * @param  {number} Company id.
     * @param  {number} Client id.
     * @param  {object} Client settings.
     * @return {object} Updated client.
     */
    updateClient(companyId, clientId, settings) {
        const request = this.createRequest(`./company/${companyId}/clients/${clientId}`, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(settings),
        });

        return this.makeRequest(request);
    }

    /**
     * Get client payments history.
     * @param {number} companyId Company id.
     * @param {number} clientId Client id.
     * @returns Client payments.
     */
    getClientPayments(companyId, clientId) {
        const request = this.createRequest(`./company/${companyId}/clients/${clientId}/payments`, {
            method: 'GET',
        });

        return this.makeRequest(request);
    }

    /**
     * Refund client payment.
     * @param {number} companyId Company id.
     * @param {number} clientId Client id.
     * @param {number} paymentId Payment id.
     * @param {object} parameters Refund parameters with amount prop.
     * @returns Refund result.
     */
    refundClientPayment(companyId, clientId, paymentId, parameters) {
        preparePayAmountsForBackend(parameters);

        const request = this.createRequest(`./company/${companyId}/clients/${clientId}/refund/${paymentId}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(parameters),
        });

        return this.makeRequest(request);
    }

    /**
     * Get client subscriptions.
     * @param {number} companyId Company id.
     * @param {number} clientId Client id.
     * @returns Client subscriptions.
     */
    getClientSubscriptions(companyId, clientId) {
        const request = this.createRequest(
            `./company/${companyId}/clients/${clientId}/subscriptions?active_only=false`,
            {
                method: 'GET',
            }
        );

        return this.makeRequest(request);
    }

    /**
     * Update current client subscription by ID.
     * @param {number} companyId Company Id.
     * @param {number} clientId Client Id.
     * @param {number} subscriptionId Subscription Id.
     * @param {object} settings Subscription settings.
     * @returns Updated subscription settings.
     */
    updateSubscription(companyId, clientId, subscriptionId, settings) {
        preparePayAmountsForBackend(settings);

        const request = this.createRequest(
            `./company/${companyId}/clients/${clientId}/subscriptions/${subscriptionId}`,
            {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(settings),
            }
        );

        return this.makeRequest(request);
    }

    /**
     * @param  {number} Company id.
     * @param  {number} Client id.
     * @param  {number} Subscription id.
     * @return {object} Object containing paused flag.
     */
    pauseSubscription(companyId, clientId, subscriptionId) {
        const request = this.createRequest(
            `./company/${companyId}/clients/${clientId}/subscriptions/${subscriptionId}/pause`,
            {
                method: 'POST',
            }
        );

        return this.makeRequest(request);
    }

    /**
     * Resume client subscription.
     * @param  {number} Company id.
     * @param  {number} Client id.
     * @param  {number} Subscription id.
     * @return {object} Object with resumed field.
     */
    resumeSubscription(companyId, clientId, subscriptionId) {
        const request = this.createRequest(
            `./company/${companyId}/clients/${clientId}/subscriptions/${subscriptionId}/resume`,
            {
                method: 'POST',
            }
        );

        return this.makeRequest(request);
    }

    /**
     * Cancel client subscription.
     * @param {number} Company id.
     * @param {number} Client id.
     * @param {number} Subscription id.
     * @returns cancelled flag and cancelled subscription object.
     */
    cancelSubscription(companyId, clientId, subscriptionId) {
        const request = this.createRequest(
            `./company/${companyId}/clients/${clientId}/subscriptions/${subscriptionId}/cancel`,
            {
                method: 'DELETE',
            }
        );

        return this.makeRequest(request);
    }

    /**
     * Get subscriptions list to email.
     */
    getUnsubscribeEmail(params) {
        const request = this.createRequest(`./unsubscribe/email`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(params),
        });

        return this.makeRequest(request);
    }

    /**
     * Unsubscribe user. No auth need.
     * @param {number} companyId Company id.
     * @param {number} clientId Client Id.
     * @param {number} subscriptionId Subscription Id.
     * @returns cancelled flag and cancelled subscription object.
     */
    unsubscribeUser(companyId, clientId, subscriptionId) {
        const request = this.createRequest(
            `./company/${companyId}/clients/${clientId}/subscriptions/${subscriptionId}/unsubscribe`,
            {
                method: 'DELETE',
            }
        );

        return fetch(request);
    }

    /**
     * Get active applications for company.
     * @param {number} companyId Company id.
     * @returns Company applications.
     */
    getApplications(companyId) {
        const request = this.createRequest(`./company/${companyId}/applications/`, {
            method: 'GET',
        });

        return this.makeRequest(request);
    }

    /**
     * Generate application token for app creation.
     * @param {number} companyId Company ID.
     * @returns {Promise<object>} Application token.
     */
    createApplicationToken(companyId) {
        const request = this.createRequest(`./company/${companyId}/applications/token`, {
            method: 'GET',
        });

        return this.makeRequest(request);
    }

    /**
     * Create a new applucation in company.
     * @param {number} companyId Company ID.
     * @param {object} params App parameters: app_name, token, type: telegram.
     * @returns {Promise<object>} Created application
     */
    createApplication(companyId, params) {
        const request = this.createRequest(`./company/${companyId}/applications/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(params),
        });

        return this.makeRequest(request, 201);
    }

    /**
     * Delete company's application by id.
     * @param {number} companyId Company ID.
     * @param {number} appId Application id.
     * @returns {Promise<object>} Object with deleted app id.
     */
    deleteApplication(companyId, appId) {
        const request = this.createRequest(`./company/${companyId}/applications/?app_id=${appId}`, {
            method: 'DELETE',
        });

        return this.makeRequest(request, 200);
    }

    exportAnalytics(companyId, dateFrom, dateTo, fileFormat = 'csv') {
        const date_from = new Date(dateFrom);
        date_from.setHours(0, 0, 0);

        const date_to = new Date(dateTo);
        date_to.setHours(23, 59, 59);

        const searchParams = new URLSearchParams({
            date_from: date_from.toISOString(),
            date_till: date_to.toISOString(),
            file_type: fileFormat,
        });

        const request = this.createRequest(`./company/${companyId}/analytics/payments/report?` + searchParams, {
            method: 'GET',
        });

        return this.protectedFetch(request);
    }
}

export default function useApi() {
    return new API();
}
